import { useEffect, useMemo, useRef } from 'react';
import { v4 as uuid } from 'uuid';
import { Pulse } from '@pasqal/core/ui/components/pulser-studio/components/Pulse';
import { useContextMenu } from '~/systems/contextMenu';
import {
  type ComputedPulse,
  type ExperimentTx,
  useExperiment
} from '~/systems/experiment';
import { layoutBuilder } from '~/systems/experiment/layout';
import { useUiState } from '~/systems/uiState';
import { assert, type Bounds, isDef, usePointer } from '~/util';
import type { ChannelKind } from '~/models/studio/channel';

interface PulseInteractiveProps {
  computedPulse: ComputedPulse;
  channelHeight: number;
  showDetuning: boolean;
  timeScale: number;
  amplitudeBounds: Bounds;
  detuningBounds: Bounds;
  channelType: ChannelKind;
}

export const PulseInteractive = ({
  computedPulse,
  channelHeight,
  showDetuning,
  timeScale,
  amplitudeBounds,
  detuningBounds,
  channelType
}: PulseInteractiveProps) => {
  const {
    atoms,
    commitedChannels,
    commitedDevice,
    commitedPulses,
    computedPulses,
    computedCommittedPulses,
    openTransaction
  } = useExperiment();
  const uiState = useUiState();
  const menu = useContextMenu();

  const txRef = useRef<ExperimentTx | null>(null);
  const pulseDragsRef = useRef<Map<string, { startPos: number }>>(new Map());
  const accumulatedDeltaRef = useRef(0);
  const lastDragRef = useRef({ x: 0, y: 0 });
  const dragAsClickRef = useRef(false);

  // Commit on unmount
  useEffect(() => () => commit(), []);

  const pulseIdx = useMemo(() => {
    const uid = computedPulse.pulse.uuid;
    return computedPulses.findIndex((p) => p.pulse.uuid === uid);
  }, [computedPulse.pulse.uuid, computedPulses]);

  function getTx() {
    if (txRef.current == null || !txRef.current.isOpen()) {
      const newTx = (txRef.current = openTransaction('dragPulses'));
      const draggedPulses = [...uiState.pulseSelection.keys()]
        .map((uid) =>
          computedCommittedPulses.find((cp) => cp.pulse.uuid === uid)
        )
        .filter(isDef);

      const drags = new Map<string, { startPos: number }>();
      draggedPulses.forEach((cp) => {
        const uid = cp.pulse.uuid;
        const pulseIndex = computedCommittedPulses.findIndex(
          (p) => p.pulse.uuid === uid
        );
        const insertBefore =
          computedCommittedPulses[pulseIndex + 1]?.pulse.uuid ?? null;
        newTx.push({ kind: 'move_pulse', uuid: uid, insertBefore });
        drags.set(uid, { startPos: cp.layout.time.min });
      });
      pulseDragsRef.current = drags;
    }
    return txRef.current;
  }

  function commit() {
    if (txRef.current != null && txRef.current.isOpen()) {
      txRef.current.commit();
    }
  }

  function revert() {
    if (txRef.current != null && txRef.current.isOpen()) {
      txRef.current.revert();
    }
  }

  function isSelected() {
    return uiState.pulseSelection.has(computedPulse.pulse.uuid);
  }

  function select(additive: boolean) {
    const pulseUuid = computedPulse.pulse.uuid;
    const selected = isSelected();
    if (selected && !additive) {
      return;
    } else if (selected && additive) {
      uiState.removePulseSelection(pulseUuid);
    } else {
      uiState.selectPulse(pulseUuid, additive);
    }
  }

  function updateMove(deltaT: number) {
    if (!commitedDevice) {
      return;
    }

    accumulatedDeltaRef.current += deltaT;
    const tx = getTx();
    const pulses = commitedPulses;

    const builder = layoutBuilder(commitedDevice, commitedChannels);
    const tempBuilder = layoutBuilder(commitedDevice, commitedChannels);

    const drags = pulseDragsRef.current;
    const draggedPulses = pulses.filter((p) => drags.has(p.uuid));
    const otherPulses = pulses.filter((p) => !drags.has(p.uuid));

    let processedPulses = 0;
    for (const pulse of draggedPulses) {
      const drag = drags.get(pulse.uuid);
      assert(drag != null);
      const desiredPos = drag.startPos + accumulatedDeltaRef.current;
      let minDistance: number | null = null;
      let pulsesToInsertBefore = 0;

      const numPulsesLeft = otherPulses.length - processedPulses;
      const doneCheckpoint = builder.saveCheckpoint();
      for (let tryBefore = 0; tryBefore <= numPulsesLeft; tryBefore++) {
        tempBuilder.loadCheckpoint(doneCheckpoint);
        for (let i = 0; i < tryBefore; i++) {
          tempBuilder.processPulse(otherPulses[processedPulses + i]);
        }
        const layout = tempBuilder.processPulse(pulse);
        const distance = Math.abs(layout.time.min - desiredPos);
        if (minDistance == null || distance < minDistance) {
          minDistance = distance;
          pulsesToInsertBefore = tryBefore;
        } else if (minDistance != null && distance > minDistance) {
          break;
        }
      }

      for (let insert = 0; insert < pulsesToInsertBefore; insert++) {
        builder.processPulse(otherPulses[processedPulses + insert]);
      }
      builder.processPulse(pulse);
      processedPulses += pulsesToInsertBefore;
    }

    const ordered = [
      ...builder.processedPulses(),
      ...otherPulses.slice(processedPulses)
    ];

    let cmdId = 0;
    for (let i = ordered.length - 1; i >= 0; i--) {
      const pulse = ordered[i];
      if (drags.has(pulse.uuid)) {
        const insertBefore = ordered[i + 1]?.uuid ?? null;
        tx.update(cmdId++, (cmd) => {
          assert(cmd.kind === 'move_pulse');
          return { kind: 'move_pulse', uuid: pulse.uuid, insertBefore };
        });
      }
    }
  }

  const pointer = usePointer((pos, ty, event) => {
    event.stopPropagation();
    if (ty === 'start') {
      accumulatedDeltaRef.current = 0;
      dragAsClickRef.current = true;
      select(event.shiftKey);
      lastDragRef.current = pos.drag;
    } else if (ty === 'move') {
      if (!isSelected()) {
        return;
      }
      if (dragAsClickRef.current) {
        uiState.setPulseDragsActive((n) => n + 1);
      }
      dragAsClickRef.current = false;
      const deltaT = (pos.drag.x - lastDragRef.current.x) * timeScale;
      lastDragRef.current = pos.drag;
      updateMove(deltaT);
    } else if (ty === 'stop') {
      if (dragAsClickRef.current) {
        revert();
      } else {
        uiState.setPulseDragsActive((n) => n - 1);
        commit();
      }
    }
  });

  function removePulse() {
    const tx = openTransaction('removePulse');
    tx.push({ kind: 'remove_pulse', uuid: computedPulse.pulse.uuid });
    tx.commit();
  }

  function removeSelectedPulses() {
    if (uiState.pulseSelection.size === 0) {
      return;
    }
    const tx = openTransaction('removeSelectedPulses');
    for (const uid of uiState.pulseSelection) {
      tx.push({ kind: 'remove_pulse', uuid: uid });
    }
    uiState.replacePulseSelection(new Set());
    tx.commit();
  }

  function duplicatePulse() {
    const tx = openTransaction('duplicatePulse');
    tx.push({
      kind: 'add_pulse',
      pulse: { ...computedPulse.pulse, uuid: uuid() },
      insertBefore: null
    });
    tx.commit();
  }

  function duplicateSelectedPulses() {
    const pulses = commitedPulses;
    const filtered = pulses.filter((p) => uiState.pulseSelection.has(p.uuid));
    if (filtered.length === 0) {
      return;
    }
    const tx = openTransaction('duplicateSelectedPulses');
    for (const pulse of filtered) {
      tx.push({
        kind: 'add_pulse',
        pulse: { ...pulse, uuid: uuid() },
        insertBefore: null
      });
    }
    tx.commit();
  }

  function contextMenu() {
    const actions: { label: string; action: () => void }[] = [];
    actions.push({
      label: `Duplicate pulse #${pulseIdx + 1}`,
      action: duplicatePulse
    });
    actions.push({
      label: `Delete pulse #${pulseIdx + 1}`,
      action: removePulse
    });
    if (isSelected() && uiState.pulseSelection.size > 1) {
      actions.push({
        label: 'Duplicate selected pulses',
        action: duplicateSelectedPulses
      });
      actions.push({
        label: 'Delete selected pulses',
        action: removeSelectedPulses
      });
    }
    menu.registerOnce(actions);
  }

  const isTargetHovered = () => {
    if (uiState.hoveredAtom === null) {
      return false;
    }
    const atom = atoms[uiState.hoveredAtom] ?? null;
    if (atom === null) {
      return false;
    }
    return atom.uuid === computedPulse.pulse.targetUuid;
  };

  const interactivity = {
    isSelected,
    isTargetHovered,
    onContextMenu: contextMenu,
    pulseDragsActive: uiState.pulseDragsActive,
    updateMove
  };

  return (
    <Pulse
      computedPulse={computedPulse}
      channelHeight={channelHeight}
      showDetuning={showDetuning}
      timeScale={timeScale}
      amplitudeBounds={amplitudeBounds}
      detuningBounds={detuningBounds}
      channelType={channelType}
      atoms={atoms}
      pointer={pointer}
      pulseIdx={pulseIdx}
      interactivity={interactivity}
    />
  );
};
